// @flow
import React from 'react'
import './styles.scss'
import config from 'config'
import { Motion, presets, spring } from 'react-motion'
import { ticks } from 'd3-array'

/**
 * The standardised width of the scale
 * @type {number}
 */
const SCALE_WIDTH: number = 100
const MIN_DELTA: number = 7.5

export type ScaleProps = {
  overallMinimum: number,
  overallMaximum: number,
  minimum: number,
  maximum: number
}

/**
 * Shows a value scale, using the overall range as well as the currently
 * shown range.
 * @param {number} overallMinimum
 * @param {number} overallMaximum
 * @param {number} minimum
 * @param {number} maximum
 * @returns {XML}
 */
export function Scale ({
  overallMinimum,
  overallMaximum,
  minimum,
  maximum
}: ScaleProps) {
  const normalize: number => number = val =>
    (val - overallMinimum) / (overallMaximum - overallMinimum)
  const Caption = ({
    value,
    formatter = config.formatAsPercent
  }: {
    value: number,
    formatter: number => string
  }) => (
    <div
      style={{
        left: `${normalize(value) * 100}%`
      }}
      className='number'
    >
      {formatter(value / 100)}
    </div>
  )
  let normalizedMinimum: number = normalize(minimum) * SCALE_WIDTH
  let normalizedMaximum: number = normalize(maximum) * SCALE_WIDTH
  let normalizedDelta = normalizedMaximum - normalizedMinimum

  const maxWindowWidth = 1200
  const windowWidthFactor =
    window.innerWidth < maxWindowWidth ? window.innerWidth : maxWindowWidth
  let tickCount = windowWidthFactor / 100
  const screenUnit = maxWindowWidth / windowWidthFactor
  let scaleTicks: number[] = ticks(overallMinimum, overallMaximum, tickCount)

  return (
    <div className='scale'>
      <div className='ta small overall caption'>
        {scaleTicks.map((tick, index) => {
          return (
            <Caption
              key={tick}
              value={tick}
              formatter={config.locale.format('.0%')}
            />
          )
        })}
      </div>
      <svg
        viewBox={`0 0 ${SCALE_WIDTH} 1`}
        preserveAspectRatio='none'
        className='strip'
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id='scale-gradient'>
            {[...Array(10).keys()].map(i => {
              return (
                <stop
                  key={`color-${i}`}
                  offset={`${i / 10 * 100}%`}
                  stopColor={config.colorGenerator(i / 10)}
                />
              )
            })}
          </linearGradient>
        </defs>
        <rect
          x='0'
          y='0'
          width='100%'
          height='100%'
          fill='#CDD2D3'
          className='background'
        />
        {!isNaN(normalizedMinimum) &&
          !isNaN(normalizedDelta) && (
          <g className='overall ticks'>
            {scaleTicks.map((tick, index) => {
              return (
                <line
                  key={tick}
                  x1={normalize(tick) * SCALE_WIDTH}
                  y1={-0.5}
                  x2={normalize(tick) * SCALE_WIDTH}
                  y2={1}
                  fill='none'
                  stroke='#999'
                  strokeLinecap='round'
                  strokeWidth={screenUnit / 5}
                />
              )
            })}
          </g>
        )}{' '}
        {!isNaN(normalizedMinimum) &&
          !isNaN(normalizedDelta) && (
          <rect
            x={normalizedMinimum}
            y={0}
            width={normalizedDelta}
            height='100%'
            fill='url(#scale-gradient)'
          />
        )}
        {!isNaN(normalizedMinimum) &&
          !isNaN(normalizedDelta) && (
          <g>
            {normalizedDelta > MIN_DELTA && (
              <line
                x1={normalize(minimum) * SCALE_WIDTH}
                y1={0}
                x2={normalize(minimum) * SCALE_WIDTH}
                y2={1.5}
                fill='none'
                stroke='#999'
                strokeLinecap='round'
                strokeWidth={screenUnit / 5}
              />
            )}
            <line
              x1={normalize(maximum) * SCALE_WIDTH}
              y1={0}
              x2={normalize(maximum) * SCALE_WIDTH}
              y2={1.5}
              fill='none'
              stroke='#999'
              strokeLinecap='round'
              strokeWidth={screenUnit / 5}
            />
          </g>
        )}
      </svg>

      <div className='ta small caption' style={{ position: 'relative' }}>
        {normalizedDelta > MIN_DELTA && <Caption value={minimum} />}
        <Caption value={maximum} />
      </div>
    </div>
  )
}

/**
 * The same as {@link scale}, but with react-motion applied.
 * @see {@link scale}
 * @param overallMinimum
 * @param overallMaximum
 * @param minimum
 * @param maximum
 * @returns {XML}
 */
export function motionScale ({
  overallMinimum,
  overallMaximum,
  minimum,
  maximum
}: ScaleProps) {
  return (
    <Motion
      defaultStyle={{
        overallMinimum: 0,
        overallMaximum: 20,
        minimum: 0,
        maximum: 20
      }}
      style={{
        overallMinimum: spring(overallMinimum),
        overallMaximum: spring(overallMaximum),
        minimum: spring(minimum, presets.gentle),
        maximum: spring(maximum, presets.gentle)
      }}
    >
      {interpolatingStyle => <Scale {...interpolatingStyle} />}
    </Motion>
  )
}

export default motionScale



// WEBPACK FOOTER //
// ./src/components/scale/component.js